import { useState, MouseEvent } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  Button,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { HiOutlineMenuAlt2 } from "react-icons/hi";
import { MdOutlineDarkMode, MdOutlineLightMode } from "react-icons/md";
import { useThemeMode } from "../../theme/ThemeModeContext";
import LogoutButton from "../buttons/LogoutButton";
import ConfirmationAlert from "../alerts/ConfirmationAlert";
import fetching from "../../helpers/fetching/fetching";
import fireAlert from "../../helpers/alerts/fireAlert";
import { removeUserToken } from "../../redux/features/user/userSlice";
import type { UserState } from "../../types/types";
import { sidebarWidth, topbarHeight } from "../../theme/tokens";

type Props = {
  onMenuClick: () => void;
};

const TopBar = ({ onMenuClick }: Props) => {
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state: UserState) => state.user.userData);
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const displayName: string =
    userData?.name || userData?.email || "المستخدم";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  // Rendered outside the Menu (see below) so it survives the menu closing:
  // MUI unmounts a Menu's children once it closes, which would otherwise
  // destroy this dialog before the user can click "confirm".
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogoutClick = () => {
    handleCloseMenu();
    setLogoutConfirmOpen(true);
  };

  const logoutUser = async () => {
    setLogoutConfirmOpen(false);
    fetching(`/logout`, userToken, "POST")
      .then((res) => res.json())
      .then(() => {
        dispatch(removeUserToken());
        fireAlert("تم تسجيل الخروج بنجاح", "success");
        navigate("/login");
      })
      .catch(() => {
        dispatch(removeUserToken());
        fireAlert("تم تسجيل الخروج بنجاح", "success");
        navigate("/login");
      });
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { xs: "100%", md: `calc(100% - ${sidebarWidth}px)` },
        marginInlineStart: { xs: 0, md: `${sidebarWidth}px` },
      }}
    >
      <Toolbar sx={{ minHeight: `${topbarHeight}px !important`, gap: 1 }}>
        <IconButton
          onClick={onMenuClick}
          sx={{ display: { xs: "inline-flex", md: "none" } }}
          aria-label="فتح القائمة"
        >
          <HiOutlineMenuAlt2 />
        </IconButton>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={mode === "dark" ? "الوضع الفاتح" : "الوضع الداكن"}>
          <IconButton onClick={toggleMode} aria-label="تبديل المظهر">
            {mode === "dark" ? <MdOutlineLightMode /> : <MdOutlineDarkMode />}
          </IconButton>
        </Tooltip>

        <Box
          onClick={handleOpenMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            paddingInline: 1,
            paddingBlock: 0.5,
            borderRadius: 2,
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main" }}>
            {displayName.charAt(0)}
          </Avatar>
          <Typography
            variant="subtitle2"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {displayName}
          </Typography>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem disabled sx={{ opacity: "1 !important" }}>
            <Box>
              <Typography variant="subtitle2">{displayName}</Typography>
              {userData?.email && (
                <Typography variant="caption" color="text.secondary">
                  {userData.email}
                </Typography>
              )}
            </Box>
          </MenuItem>
          <LogoutButton onClick={handleLogoutClick} />
        </Menu>

        <ConfirmationAlert
          open={logoutConfirmOpen}
          setOpen={setLogoutConfirmOpen}
          title={"تسجيل خروج"}
          DialogActionNodes={
            <>
              <Button onClick={logoutUser} variant="contained">
                تسجيل الخروج
              </Button>
              <Button onClick={() => setLogoutConfirmOpen(false)}>
                الغاء
              </Button>
            </>
          }
        >
          <Typography variant="body2">
            هل انت متاكد انك تريد تسجيل الخروج
          </Typography>
        </ConfirmationAlert>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
